import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, List, Map as MapIcon } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { dedupedGet } from 'shared/services/api/dedupedGet';
import { HighlightContext, HighlightText } from 'shared/components/HighlightText';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import DrawerScrim, { DRAWER_BOUNDS_CLASS } from 'shared/components/ui/DrawerScrim';
import AgentMultiSelect from 'features/agent-analytics/components/AgentMultiSelect';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import ClusterMap from 'features/agent-analytics/components/ClusterMap';
import ClustersTable from 'features/agent-analytics/components/ClustersTable';
import TableFooter from 'shared/components/tables/TableFooter';
import { useBreadcrumb, useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import {
  activitySearchInputClass,
  activitySearchWrapperClass,
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

const RUN_PAGE_SIZE = 25;

function useSelectedTeam() {
  const [team, setTeam] = useState(() =>
    typeof window === 'undefined' ? '' : localStorage.getItem('selectedTeam') || ''
  );
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selectedTeam') setTeam(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return team;
}

// Red → amber → green lerp on a 0..1 score. Mirrors ClustersTable.satisfactionColor.
function satisfactionColor(score01) {
  const v = Math.max(0, Math.min(1, Number(score01) || 0));
  const stops = [
    { t: 0, h: 0, s: 78, l: 50 },
    { t: 0.5, h: 42, s: 90, l: 50 },
    { t: 1, h: 138, s: 62, l: 42 },
  ];
  let a = stops[0];
  let b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (v >= stops[i].t && v <= stops[i + 1].t) { a = stops[i]; b = stops[i + 1]; break; }
  }
  const span = b.t - a.t || 1;
  const k = (v - a.t) / span;
  return `hsl(${(a.h + (b.h - a.h) * k).toFixed(1)} ${(a.s + (b.s - a.s) * k).toFixed(1)}% ${(a.l + (b.l - a.l) * k).toFixed(1)}%)`;
}

function pct(v) { return v == null ? '—' : `${Math.round(Number(v) * 100)}%`; }

// agent_runs.input arrives ::text — often a JSON string. Pull a readable line.
function previewText(s) {
  if (!s) return '';
  const str = String(s);
  try {
    const o = JSON.parse(str);
    if (o && typeof o === 'object') {
      if (typeof o.query === 'string') return o.query;
      if (typeof o.content === 'string') return o.content;
      if (Array.isArray(o)) return o.map((m) => (m && m.content) || '').filter(Boolean).join(' ');
    }
  } catch { /* not JSON: fall through */ }
  return str;
}

// Map payloads by team|agent, so returning to the map within a few minutes is instant.
const MAP_CACHE = new Map();
const MAP_CACHE_MS = 5 * 60 * 1000;

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-2.5 border-b border-[color:var(--border-default)] pb-1.5 text-[14px] font-semibold tracking-[-0.005em] text-[color:var(--text-primary)]">{title}</h3>
      {children}
    </section>
  );
}

/** Colour only where something is wrong: a poor score reads red, the rest stay ink. */
function scoreTone(score01) {
  return score01 != null && Number(score01) < 0.5 ? 'var(--feedback-error)' : 'var(--text-primary)';
}

// Columns for the shared ClustersTable so the capabilities list matches Issues.
const CAPABILITY_COLUMNS = [
  {
    key: 'name',
    header: 'Capability',
    render: (c) => (
      <div className="whitespace-nowrap font-medium text-[color:var(--text-primary)]">{c.name}</div>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    render: (c) => (
      <div className="max-w-[480px] truncate text-[color:var(--text-secondary)]" title={c.definition || ''}>
        {c.definition || '—'}
      </div>
    ),
  },
  {
    key: 'runs',
    header: 'Runs',
    render: (c) => <span className="tabular-nums text-[color:var(--text-primary)]">{Number(c.run_count) || 0}</span>,
  },
  {
    key: 'satisfaction',
    header: 'Satisfaction',
    render: (c) => (
      <span className="tabular-nums font-semibold" style={{ color: satisfactionColor(c.satisfaction_score) }}>
        {pct(c.satisfaction_score)}
      </span>
    ),
  },
  {
    key: 'subject',
    header: 'Top subject',
    render: (c) => {
      const s = (c.subjects || [])[0];
      return s
        ? <span className="whitespace-nowrap text-[color:var(--text-muted)]">{s.name} <span className="tabular-nums">{s.runs}</span></span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    },
  },
];

/**
 * Capabilities page (capability model). Compact table; clicking a row opens a
 * right-side drawer with the analysis. Subjects in the drawer expand to a
 * paginated runs list; clicking a run opens the run-detail popup.
 */
export default function CapabilitiesPage() {
  const teamId = useSelectedTeam();
  useBreadcrumb([{ label: 'Capabilities' }]);

  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'map'

  // Deep-link from global search: ?capabilityId= opens the drawer once the
  // list loads; ?highlight= marks the matched text inside it. One-shot —
  // after hydrating, in-page selection owns the drawer.
  const [searchParams] = useSearchParams();
  const highlightTerm = searchParams.get('highlight') || '';
  const [deepLinkDone, setDeepLinkDone] = useState(false);
  useEffect(() => {
    if (deepLinkDone) return;
    const id = searchParams.get('capabilityId');
    if (!id || !capabilities.length) return;
    const cap = capabilities.find((c) => String(c.id) === id);
    if (cap) {
      setSelected(cap);
      // The deep-linked capability may be filtered out by the default
      // single-agent preselection — widen to its agent so the row is visible.
      setSelectedAgents((prev) => (prev.includes(cap.agent_name) ? prev : [cap.agent_name]));
    }
    setDeepLinkDone(true);
  }, [capabilities, searchParams, deepLinkDone]);
  const [mapData, setMapData] = useState({ clusters: [], conversations: [] });
  const [mapLoading, setMapLoading] = useState(false);

  // Subject → runs drill-down state.
  const [openSubject, setOpenSubject] = useState(null);
  const [subjectRuns, setSubjectRuns] = useState([]);
  const [runOffset, setRunOffset] = useState(0);
  const [runTotal, setRunTotal] = useState(0);
  const [runHasMore, setRunHasMore] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  // ---- agent list -----------------------------------------------------
  useEffect(() => {
    if (!teamId) return undefined;
    const ac = new AbortController();
    axiosInstance
      .get('/agent-runs/agents', { params: { team_id: teamId }, signal: ac.signal })
      .then((res) => {
        const list = res.data?.agents || [];
        // All agents by default, like Traces.
        setAgentOptions(list);
      })
      .catch(() => setAgentOptions([]));
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // ---- capabilities ---------------------------------------------------
  const load = useCallback(async () => {
    if (!teamId) { setError('Select a team to view capabilities.'); setCapabilities([]); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await dedupedGet('/agent-runs/capabilities', { params: { team_id: teamId } });
      setCapabilities(data?.capabilities || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load capabilities.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  // Map data — fetched lazily when the Map view is active, and kept for a few
  // minutes per agent so flipping List ↔ Map is instant and costs no request.
  useEffect(() => {
    if (view !== 'map' || !teamId) return undefined;
    let cancel = false;
    const agent = selectedAgents.length === 1 ? selectedAgents[0] : undefined;
    const key = `${teamId}|${agent || ''}`;
    const hit = MAP_CACHE.get(key);
    if (hit && Date.now() - hit.at < MAP_CACHE_MS) {
      setMapData(hit.data);
      setMapLoading(false);
      return undefined;
    }
    setMapLoading(true);
    dedupedGet('/agent-runs/capabilities/map', { params: { team_id: teamId, agent_name: agent } })
      .then((res) => {
        const data = { clusters: res.data?.clusters || [], conversations: res.data?.conversations || [] };
        MAP_CACHE.set(key, { at: Date.now(), data });
        if (!cancel) setMapData(data);
      })
      .catch(() => { if (!cancel) setMapData({ clusters: [], conversations: [] }); })
      .finally(() => { if (!cancel) setMapLoading(false); });
    return () => { cancel = true; };
  }, [view, teamId, selectedAgents]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { if (selectedRunId) setSelectedRunId(null); else setSelected(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, selectedRunId]);

  const agentDropdownOptions = useMemo(
    () => agentOptions.map((a) => ({ value: a.agent_name, label: a.agent_name })),
    [agentOptions],
  );

  const rows = useMemo(() => {
    let result = capabilities;
    if (selectedAgents.length > 0) result = result.filter((c) => selectedAgents.includes(c.agent_name));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.definition || '').toLowerCase().includes(q) ||
        (c.subjects || []).some((s) => (s.name || '').toLowerCase().includes(q)));
    }
    return [...result].sort((a, b) => (Number(b.run_count) || 0) - (Number(a.run_count) || 0));
  }, [capabilities, selectedAgents, searchQuery]);

  const active = useMemo(
    () => (selected ? capabilities.find((c) => c.id === selected.id) || selected : null),
    [selected, capabilities],
  );

  // Reset drill-down when switching capability / closing drawer.
  useEffect(() => {
    setOpenSubject(null); setSubjectRuns([]); setRunOffset(0); setRunTotal(0); setRunHasMore(false); setSelectedRunId(null);
  }, [selected]);

  const fetchRuns = useCallback(async (capId, subject, offset) => {
    const { data } = await axiosInstance.get(`/agent-runs/capabilities/${capId}/runs`, {
      params: { team_id: teamId, subject, limit: RUN_PAGE_SIZE, offset },
    });
    return data || {};
  }, [teamId]);

  const toggleSubject = async (capId, subjectName) => {
    if (openSubject === subjectName) { setOpenSubject(null); return; }
    setOpenSubject(subjectName);
    setSubjectRuns([]); setRunOffset(0); setRunTotal(0); setRunHasMore(false); setRunLoading(true);
    try {
      const data = await fetchRuns(capId, subjectName, 0);
      const incoming = data.runs || [];
      setSubjectRuns(incoming);
      setRunTotal(data.pagination?.total ?? incoming.length);
      setRunOffset(incoming.length);
      setRunHasMore(Boolean(data.pagination?.hasMore));
    } catch { setSubjectRuns([]); } finally { setRunLoading(false); }
  };

  const loadMoreRuns = async (capId) => {
    if (runLoading || !runHasMore) return;
    setRunLoading(true);
    try {
      const data = await fetchRuns(capId, openSubject, runOffset);
      const incoming = data.runs || [];
      setSubjectRuns((prev) => [...prev, ...incoming]);
      setRunTotal(data.pagination?.total ?? runTotal);
      setRunOffset(runOffset + incoming.length);
      setRunHasMore(Boolean(data.pagination?.hasMore));
    } catch { /* keep list */ } finally { setRunLoading(false); }
  };

  function RunRow({ r }) {
    // One line per run: a status dot and what was asked. The whole line opens the run.
    const failed = String(r.status || '').toLowerCase() === 'error';
    return (
      <button
        type="button"
        onClick={() => setSelectedRunId(r.run_id)}
        title={failed ? 'Failed' : 'OK'}
        className="flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-left text-[13px] transition-colors hover:bg-surface-hover"
      >
        <span
          aria-label={failed ? 'Failed' : 'OK'}
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: failed ? 'var(--feedback-error)' : 'var(--feedback-success)' }}
        />
        <span className="min-w-0 flex-1 truncate text-[color:var(--text-primary)]">{previewText(r.input) || '—'}</span>
      </button>
    );
  }

  // Render the controls inline with the breadcrumb title ("Capabilities") via the
  // top bar's actions slot — one horizontal line with the page path.
  // Every block carries a min width and is allowed to shrink; the search is the
  // one that gives first. Fixed widths in a slot that couldn't compress meant
  // this toolbar ran off the right edge of the top bar on a narrow window —
  // most visible in Map view, where the full-bleed canvas sits right under it.
  useBreadcrumbActions(
    <div className="flex min-w-0 items-center gap-2">
      <div className="inline-flex h-8 flex-none items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <div className="flex items-center gap-1.5 px-3 text-[12px]">
          <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{rows.length.toLocaleString()}</span>
          <span className="text-[color:var(--text-muted)]">{rows.length === 1 ? 'capability' : 'capabilities'}</span>
        </div>
      </div>
      <div className="inline-flex h-8 flex-none items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <button type="button" onClick={() => setView('list')} className={`flex items-center gap-1 px-2.5 text-[12px] ${view === 'list' ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
          <List size={13} /> List
        </button>
        <button type="button" onClick={() => setView('map')} className={`flex items-center gap-1 border-l border-[color:var(--border-default)] px-2.5 text-[12px] ${view === 'map' ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
          <MapIcon size={13} /> Map
        </button>
      </div>
      <div className={`${activitySearchWrapperClass} w-[220px] min-w-[110px] shrink`}>
        <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
        <input className={activitySearchInputClass} placeholder="Search capabilities…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
      </div>
      <div className="shrink-0">
        <AgentMultiSelect teamId={teamId} value={selectedAgents} onChange={setSelectedAgents} agents={agentDropdownOptions.map((o) => o.value)} />
      </div>
    </div>
  );

  return (
    <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Body (controls live in the top-bar actions slot, inline with the title) */}
      <div className="min-h-0 flex-1 overflow-auto">
        {view === 'map' ? (
          <div className="h-full p-3">
          <ClusterMap
            clusters={mapData.clusters}
            conversations={mapData.conversations}
            loading={mapLoading}
            onSelect={(c) => { const full = capabilities.find((x) => x.id === c.id) || c; setSelected(full); }}
            onSelectConversation={(conv) => setSelectedRunId(conv.run_id || conv.id)}
            minViewportHeight="0"
          />
          </div>
        ) : loading ? (
          <PageLoader label="Loading capabilities" />
        ) : error ? (
          <div className="m-4 rounded-md border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12px] text-[color:var(--feedback-error)]">{error}</div>
        ) : (
          <div className="p-3">
            <ClustersTable
              clusters={rows}
              columns={CAPABILITY_COLUMNS}
              onRowClick={(c) => setSelected(c)}
              selectedId={active?.id ?? null}
              rowKey={(c) => c.id}
              emptyMessage="No capabilities yet for this team/agent."
            />
          </div>
        )}
      </div>

      {/* Side drawer: beside the table, within the app's content panel, so the
          list stays readable and the rail stays usable while it is open. */}
      {active ? (
        <div className={`${DRAWER_BOUNDS_CLASS} z-[40]`} role="region" aria-label="Capability detail">
          <DrawerScrim onClose={() => setSelected(null)} />
          <section
            className="pointer-events-auto relative flex h-full w-[min(92vw,640px)] flex-col overflow-y-auto border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[color:var(--border-default)] bg-surface px-5 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-[16px] font-semibold text-[color:var(--text-primary)]">
                  <HighlightContext.Provider value={highlightTerm}><HighlightText text={active.name} /></HighlightContext.Provider>
                </h2>
              </div>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close" className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                <X size={16} />
              </button>
            </header>

            <div className="flex flex-col gap-5 px-5 py-4 text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
              <div className="grid grid-cols-3 gap-6">
                {[
                  { label: 'Agent', value: active.agent_name || '—' },
                  { label: 'Runs', value: (Number(active.run_count) || 0).toLocaleString() },
                  { label: 'Satisfaction', value: pct(active.satisfaction_score), color: scoreTone(active.satisfaction_score) },
                ].map((k) => (
                  <div key={k.label} className="min-w-0">
                    <p className="text-[11px] text-[color:var(--text-muted)]">{k.label}</p>
                    <p className="mt-1 truncate text-[15px] font-semibold tabular-nums" style={{ color: k.color || 'var(--text-primary)' }}>{k.value}</p>
                  </div>
                ))}
              </div>

              {active.definition ? (
                <Section title="What this capability is">
                  <p><HighlightContext.Provider value={highlightTerm}><HighlightText text={active.definition} /></HighlightContext.Provider></p>
                </Section>
              ) : null}

              {active.analysis ? (
                <>
                  {active.analysis.summary ? <Section title="Summary"><p>{active.analysis.summary}</p></Section> : null}
                  {active.analysis.what_users_want ? <Section title="User goal"><p>{active.analysis.what_users_want}</p></Section> : null}
                  {Array.isArray(active.analysis.friction_points) && active.analysis.friction_points.length ? (
                    <Section title="Friction points">
                      <ul className="ml-4 list-disc space-y-0.5">{active.analysis.friction_points.map((f, i) => <li key={i}>{f}</li>)}</ul>
                    </Section>
                  ) : null}
                  {Array.isArray(active.analysis.worst_examples) && active.analysis.worst_examples.length ? (
                    <Section title="Worst examples">
                      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
                        <table className="w-full text-left">
                          <thead>
                            <tr className={tableHeaderRowClass}>
                              <th className={`${tableHeaderCellClass} w-[96px]`}>Run</th>
                              <th className={tableHeaderCellClass}>What went wrong</th>
                            </tr>
                          </thead>
                          <tbody>
                            {active.analysis.worst_examples.map((e, i) => (
                              <tr key={i} onClick={() => setSelectedRunId(e.run_id)} className={tableRowClass}>
                                <td className={`${tableDataCellClass} py-2 align-top font-mono text-[11px] text-[color:var(--text-muted)]`}>{String(e.run_id || '').slice(0, 8)}</td>
                                <td className={`${tableDataCellClass} py-2 leading-snug`}>{e.issue || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Section>
                  ) : null}
                  {Array.isArray(active.analysis.recommendations) && active.analysis.recommendations.length ? (
                    <Section title="Recommendations">
                      <ul className="ml-4 list-disc space-y-0.5">{active.analysis.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
                    </Section>
                  ) : null}
                  {active.analysis.satisfaction_reason ? (
                    <Section title="Why this satisfaction"><p className="text-[color:var(--text-muted)]">{active.analysis.satisfaction_reason}</p></Section>
                  ) : null}
                </>
              ) : (
                <p className="italic text-[color:var(--text-muted)]">Not yet analysed.</p>
              )}

              {/* Subject breakdown: click a subject to drill into its runs */}
              {(active.subjects || []).length ? (
                <Section title="Subjects">
                  <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
                    {active.subjects.map((s, i) => {
                      const open = openSubject === s.name;
                      return (
                        <div key={s.name} className={i ? 'border-t border-[color:var(--border-default)]' : ''}>
                          <button type="button" onClick={() => toggleSubject(active.id, s.name)} className="flex h-9 w-full items-center justify-between gap-2 px-3 text-left text-[13px] transition-colors hover:bg-surface-hover">
                            <span className="flex items-center gap-1.5 font-medium text-[color:var(--text-primary)]">
                              <ChevronDown size={13} className={`text-[color:var(--text-muted)] transition-transform ${open ? '' : '-rotate-90'}`} />
                              {s.name}
                            </span>
                            <span className="flex items-center gap-3 tabular-nums">
                              <span className="text-[color:var(--text-muted)]">{s.runs} runs</span>
                              <span className="w-10 text-right font-medium" style={{ color: scoreTone(s.satisfaction) }}>{pct(s.satisfaction)}</span>
                            </span>
                          </button>
                          {open ? (
                            <div className="border-t border-[color:var(--border-default)] px-2 py-1">
                              {runLoading && subjectRuns.length === 0 ? (
                                // Sized to the row it loads into. The default is
                                // 120px — in this h-16 (64px) slot it painted
                                // over the subjects above and below it.
                                <div className="flex h-16 items-center justify-center"><TrodoLoader size={32} /></div>
                              ) : subjectRuns.length === 0 ? (
                                <p className="px-3 py-3 text-center text-[11px] text-[color:var(--text-muted)]">No runs.</p>
                              ) : (
                                <>
                                  {subjectRuns.map((r) => <RunRow key={r.run_id} r={r} />)}
                                  <TableFooter
                                    shown={subjectRuns.length}
                                    total={runTotal}
                                    hasMore={runHasMore}
                                    loading={runLoading}
                                    onLoadMore={() => loadMoreRuns(active.id)}
                                    className="px-2 pb-2"
                                  />
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {/* Run detail popup */}
      {selectedRunId ? (
        <AgentRunDetailPage runId={selectedRunId} teamId={teamId} onClose={() => setSelectedRunId(null)} onChange={() => {}} />
      ) : null}
    </main>
  );
}
